import React, { Fragment, useMemo, useState } from 'react';
import AppTable from '../../../../constants/AppTable';
import { LoadingSpinner } from '../../../../constants/AppLoading';
import DialogBox from '../../../../constants/DialogBox';
import { Grid } from '@mui/material';
import InputTextBox from '../../../../constants/InputTextBox';
import { useFormik } from 'formik';
import NepaliInputBox from '../../../../constants/NepaliInputBox';
import { handleNepaliInputChange } from '../../../../helpers/handleChange';
import { EndBaseLine } from '../../../../constants/AppSpace';
import { Button } from '../../../../constants/Button';
import { putDataToTheServer } from '../../../../helpers/api';
import AppURL from '../../../../constants/AppURL';
import { ToastSuccess, ToastWarning } from '../../../../constants/Toast';
import { Supplier } from '../../../../helpers/models/Models';
import DefaultSelectInputBox from '../../../../constants/DefaultSelectBox';
import { YesNoOptions } from '../../../../constants/Options';

interface ItemStoreListViewProps {
    results: {
        results: Supplier[];
        previous: string | null;
        next: string | null;
    } | null;
    loading: boolean;
    error: string | null;
    setOnURL: React.Dispatch<React.SetStateAction<string>>;
    handleFormSuccess: () => void;
}

const ItemSuppilerListView: React.FC<ItemStoreListViewProps> = ({ results, loading, error, handleFormSuccess, setOnURL }) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState<Supplier | null>(null);

    const columns = [
        { header: 'SN', accessor: 'sn', sortable: false },
        { header: 'Suppiler Name', accessor: 'supp_name', sortable: true },
        { header: 'Suppiler Name(Nepali)', accessor: 'supp_name_nep', sortable: false },
        { header: 'Suppiler Address', accessor: 'sup_address', sortable: false },
        { header: 'Suppiler Address(Nepali)', accessor: 'sup_address_nep', sortable: false },
        { header: 'Suppiler Phone No.', accessor: 'sup_phone', sortable: false },
        { header: 'Suppiler E-mail', accessor: 'sup_email', sortable: false },
        { header: 'Contact P. name', accessor: 'per_name', sortable: false },
        { header: 'CP. name(nepali)', accessor: 'per_name_nep', sortable: false },
        { header: 'CP. Phone No.', accessor: 'per_phone', sortable: false },
        { header: 'CP. E-mail.', accessor: 'per_email', sortable: false },
        { header: 'Actions', accessor: 'actions', action: true },
    ];
    const modifiedData = useMemo(() => {
        if (!results) return [];
        return results.results.map((item: Supplier) => ({
            sn: item.sn,
            supp_name: item.supp_name,
            supp_name_nep: item.supp_name_nep,
            sup_address: item.sup_address,
            sup_address_nep: item.sup_address_nep,
            sup_phone: item.sup_phone,
            sup_email: item.sup_email,
            per_name: item.per_name,
            per_name_nep: item.per_name_nep,
            per_phone: item.per_phone,
            per_email: item.per_email,
            branch: item.branch,
            remarks: item.remarks
        }));
    }, [results]);

    const handleEdit = (item: Supplier | null) => {
        setCurrentItem(item); // Use logical OR to default to an empty array if item is null or undefined
        setDialogOpen(true);
    };



    const formik = useFormik({
        initialValues: {
            sn: currentItem?.sn || "",
            supp_name: currentItem?.supp_name || "",
            supp_name_nep: currentItem?.supp_name_nep || "",
            sup_phone: currentItem?.sup_phone || "",
            sup_email: currentItem?.sup_email || "",
            sup_address: currentItem?.sup_address || "",
            sup_address_nep: currentItem?.sup_address_nep || "",
            per_name: currentItem?.per_name || "",
            per_name_nep: currentItem?.per_name_nep || "",
            per_phone: currentItem?.per_phone || "",
            per_email: currentItem?.per_email || "",
            remarks: currentItem?.remarks || "",
            branch: currentItem?.branch || 1, // Assuming branch is optional with a default value
        },
        // validationSchema: readerEditValidationSchema,
        enableReinitialize: true,
        onSubmit: async (values, action) => {
            const formData = new FormData();
            Object.keys(values).forEach((key) => {
                const value = (values as Record<string, any>)[key];
                if (value !== undefined && value !== null && value !== "") {
                    formData.append(key, value);
                }
            });
            try {
                await putDataToTheServer(AppURL.InventoryURL + 'inventory/product/item/suppiler/retrieve/update/view/' + currentItem?.sn, formData).then((res) => {
                    if (res?.status === 200) {
                        handleFormSuccess();
                        setDialogOpen(false);
                        ToastSuccess({ message: res?.message });
                    } else if (res?.status === 400) {
                        ToastWarning({ message: res?.message });
                    }
                    else {
                        ToastWarning({ message: "Some Issue" });
                    }
                });
            } catch (error) {
                console.log(error);
            }
        },
    });

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <LoadingSpinner />
            </div>
        );
    }

    if (error) {
        return <p>Error loading data: {error}</p>;
    }

    return (
        <Fragment>
            <AppTable
                columns={columns}
                data={modifiedData}
                onEdit={handleEdit}
                onNext={() => results?.next && setOnURL(results.next)}
                onPrev={() => results?.previous && setOnURL(results.previous)}
            />
            {dialogOpen && currentItem && (
                <DialogBox open={dialogOpen} headerName='Product Supplier Edit Update' close={() => setDialogOpen(false)}>
                    <form onSubmit={formik.handleSubmit}>
                        <Grid container columnSpacing={3} rowSpacing={0.8}>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='supp_name'
                                    label='Supplier Name'
                                    handleChange={formik.handleChange}
                                    value={formik.values.supp_name}
                                    error={typeof formik.errors.supp_name === 'string' ? formik.errors.supp_name : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.supp_name}
                                    required
                                    placeholder='Enter Supplier Name'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <NepaliInputBox
                                    label='Supplier Name (Nepali)'
                                    name='supp_name_nep'
                                    value={formik.values.supp_name_nep}
                                    handleChange={(e) => formik.setFieldValue('supp_name_nep', e.target.value)}
                                    required
                                    handleBlur={formik.handleBlur}
                                    error={typeof formik.errors.supp_name_nep === 'string' ? formik.errors.supp_name_nep : undefined}
                                    placeholder='Enter Supplier Name (Nepali)'
                                    touched={formik.touched.supp_name_nep}
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='sup_address'
                                    label='Supplier Address'
                                    handleChange={formik.handleChange}
                                    value={formik.values.sup_address}
                                    error={typeof formik.errors.sup_address === 'string' ? formik.errors.sup_address : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.sup_address}
                                    required
                                    placeholder='Enter Supplier Address'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <NepaliInputBox
                                    label='Supplier Address (Nepali)'
                                    name='sup_address_nep'
                                    value={formik.values.sup_address_nep}
                                    handleChange={(e) => formik.setFieldValue('sup_address_nep', e.target.value)}
                                    required
                                    handleBlur={formik.handleBlur}
                                    error={typeof formik.errors.sup_address_nep === 'string' ? formik.errors.sup_address_nep : undefined}
                                    placeholder='Enter Supplier Address (Nepali)'
                                    touched={formik.touched.sup_address_nep}
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='sup_phone'
                                    label='Phone No.'
                                    handleChange={formik.handleChange}
                                    value={formik.values.sup_phone}
                                    error={typeof formik.errors.sup_phone === 'string' ? formik.errors.sup_phone : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.sup_phone}
                                    placeholder='Enter Phone No.'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='email'
                                    name='sup_email'
                                    label='E-mail'
                                    handleChange={formik.handleChange}
                                    value={formik.values.sup_email}
                                    error={typeof formik.errors.sup_email === 'string' ? formik.errors.sup_email : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.sup_email}
                                    placeholder='Enter E-mail'
                                />
                            </Grid>





                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='per_name'
                                    label='Contact Person Name'
                                    handleChange={formik.handleChange}
                                    value={formik.values.per_name}
                                    error={typeof formik.errors.per_name === 'string' ? formik.errors.per_name : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.per_name}
                                    required
                                    placeholder='Enter Contact Person Name'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <NepaliInputBox
                                    label='Contact Person Name (Nepali)'
                                    name='sup_address_nep'
                                    value={formik.values.per_name_nep}
                                    handleChange={(e) => formik.setFieldValue('per_name_nep', e.target.value)}
                                    required
                                    handleBlur={formik.handleBlur}
                                    error={typeof formik.errors.per_name_nep === 'string' ? formik.errors.per_name_nep : undefined}
                                    placeholder='Enter Contact Person Name (Nepali)'
                                    touched={formik.touched.per_name_nep}
                                />
                            </Grid>

                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='per_phone'
                                    label='Contact Person Phone No.'
                                    handleChange={formik.handleChange}
                                    value={formik.values.per_phone}
                                    error={typeof formik.errors.per_phone === 'string' ? formik.errors.per_phone : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.per_phone}
                                    placeholder='Enter Phone No.'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='email'
                                    name='per_email'
                                    label='Contact Person E-mail'
                                    handleChange={formik.handleChange}
                                    value={formik.values.per_email}
                                    error={typeof formik.errors.per_email === 'string' ? formik.errors.per_email : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.per_email}
                                    placeholder='Enter E-mail'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='remarks'
                                    label='Remarks'
                                    handleChange={formik.handleChange}
                                    value={formik.values.remarks}
                                    error={typeof formik.errors.remarks === 'string' ? formik.errors.remarks : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.remarks}
                                    placeholder='Enter Remarks'
                                />
                            </Grid>

                            <Grid item xl={12} lg={12} sm={12} md={12} xs={12}>
                                <EndBaseLine top={18}>
                                    <Button type='submit' btn_type='update' el='button' />
                                </EndBaseLine>
                            </Grid>
                        </Grid>
                    </form>
                </DialogBox>
            )}
        </Fragment>
    );
};

export default ItemSuppilerListView;
